<c-container fluid>
  <!-- Header + month picker -->
  <div class="d-flex flex-wrap align-items-end gap-2 mb-3">
    <div class="me-auto">
      <h4 class="mb-0">{{ 'card_partner_admin.title' | translate }}</h4>
      <div class="small text-medium-emphasis">{{ 'card_partner_admin.subtitle' | translate }}</div>
      @if (overview()?.last_synced_at; as synced) {
        <div class="small text-medium-emphasis">{{ 'card_partner_admin.last_synced' | translate }}: {{ dateTime(synced) }}</div>
      }
    </div>
    <div class="month-picker d-flex flex-wrap align-items-center gap-1">
      <button cButton color="secondary" variant="outline" size="sm" (click)="prevMonth()" [attr.aria-label]="'card_partner_admin.prev_month' | translate">‹</button>
      <input
        cFormControl
        sizing="sm"
        type="month"
        class="month-input"
        [max]="currentMonth"
        [ngModel]="isAllTime() ? '' : month()"
        (ngModelChange)="$event && setMonth($event)"
        [attr.aria-label]="'card_partner_admin.month' | translate"
      />
      <button cButton color="secondary" variant="outline" size="sm" (click)="nextMonth()" [disabled]="!canGoNext()" [attr.aria-label]="'card_partner_admin.next_month' | translate">›</button>
      <button cButton size="sm" [color]="isAllTime() ? 'primary' : 'secondary'" [variant]="isAllTime() ? undefined : 'outline'" (click)="setMonth('all')">
        {{ 'card_partner_admin.all_time' | translate }}
      </button>
    </div>
  </div>

  @if (overviewError()) {
    <div class="alert alert-danger py-2">{{ 'card_partner_admin.load_error' | translate }}</div>
  }

  @if (overviewLoading() && !overview()) {
    <div class="text-center py-4"><c-spinner></c-spinner></div>
  }

  @if (overview(); as o) {
    <!-- KPI tiles -->
    <c-row class="g-3 mb-3" [class.opacity-50]="overviewLoading()">
      <c-col xs="6" md="4" xl="2">
        <c-card class="stat-card"><c-card-body>
          <div class="stat-value">{{ o.accounts.total }}</div>
          <div class="stat-label">{{ 'card_partner_admin.kpi_accounts' | translate }}</div>
          <div class="stat-sub">+{{ o.accounts.new_in_period }} {{ 'card_partner_admin.kpi_new_accounts' | translate }}</div>
        </c-card-body></c-card>
      </c-col>
      <c-col xs="6" md="4" xl="2">
        <c-card class="stat-card"><c-card-body>
          <div class="stat-value">{{ o.cards.activations_in_period }}</div>
          <div class="stat-label">{{ 'card_partner_admin.kpi_activations' | translate }}</div>
          <div class="stat-sub">{{ o.cards.activated_total }} {{ 'card_partner_admin.kpi_activated_total' | translate }}</div>
        </c-card-body></c-card>
      </c-col>
      <c-col xs="6" md="4" xl="2">
        <c-card class="stat-card"><c-card-body>
          <div class="stat-value text-info">{{ money(o.transactions.spend_volume) }}</div>
          <div class="stat-label">{{ 'card_partner_admin.kpi_spend' | translate }} (USD)</div>
          <div class="stat-sub">{{ o.transactions.spend_count }} {{ 'card_partner_admin.kpi_payments' | translate }} · Ø {{ o.transactions.average_ticket ? money(o.transactions.average_ticket) : '—' }}</div>
        </c-card-body></c-card>
      </c-col>
      <c-col xs="6" md="4" xl="2">
        <c-card class="stat-card"><c-card-body>
          <div class="stat-value text-warning">{{ money(o.transactions.fee_volume) }}</div>
          <div class="stat-label">{{ 'card_partner_admin.kpi_fees' | translate }} (USD)</div>
          <div class="stat-sub">{{ o.transactions.failed_count }} {{ 'card_partner_admin.kpi_declines' | translate }}</div>
        </c-card-body></c-card>
      </c-col>
      <c-col xs="6" md="4" xl="2">
        <c-card class="stat-card"><c-card-body>
          <div class="stat-value text-success">{{ money(depositsUsdt()) }}</div>
          <div class="stat-label">{{ 'card_partner_admin.kpi_deposits' | translate }} USDT</div>
        </c-card-body></c-card>
      </c-col>
      <c-col xs="6" md="4" xl="2">
        <c-card class="stat-card"><c-card-body>
          <div class="stat-value text-success">{{ money(depositsUsdc()) }}</div>
          <div class="stat-label">{{ 'card_partner_admin.kpi_deposits' | translate }} USDC</div>
        </c-card-body></c-card>
      </c-col>
    </c-row>

    <!-- Charts: last 12 months / last 30 days -->
    @if (chartData(); as monthly) {
      <c-card class="mb-3">
        <c-card-header class="pb-0 border-0">
          <c-tabs [activeItemKey]="chartTab()" (activeItemKeyChange)="chartTab.set($any($event))">
            <c-tabs-list variant="tabs">
              <button cTab itemKey="months">{{ 'card_partner_admin.series_title' | translate }}</button>
              <button cTab itemKey="days">{{ 'card_partner_admin.series_days_title' | translate }}</button>
            </c-tabs-list>
          </c-tabs>
        </c-card-header>
        <c-card-body>
          <div class="chart-box">
            @if (chartTab() === 'days') {
              @if (dailyChartData(); as daily) {
                <c-chart type="bar" [data]="daily" [options]="chartOptions"></c-chart>
              }
            } @else {
              <c-chart type="bar" [data]="monthly" [options]="chartOptions"></c-chart>
            }
          </div>
          <div class="small text-medium-emphasis mt-2">
            {{ (chartTab() === 'days' ? 'card_partner_admin.series_days_hint' : 'card_partner_admin.series_hint') | translate }}
          </div>
        </c-card-body>
      </c-card>
    }

    <c-row class="g-3 mb-3">
      <!-- Accounts -->
      <c-col xs="12" lg="6">
        <c-card class="h-100">
          <c-card-header><strong>{{ 'card_partner_admin.accounts_title' | translate }}</strong></c-card-header>
          <c-card-body>
            <div class="d-flex flex-wrap gap-4 mb-3 small">
              <div>{{ 'card_partner_admin.linked' | translate }}: <strong>{{ o.accounts.linked }}</strong></div>
              <div>{{ 'card_partner_admin.unlinked' | translate }}: <strong>{{ o.accounts.unlinked }}</strong></div>
              <div>{{ 'card_partner_admin.kpi_new_accounts' | translate }}: <strong>{{ o.accounts.new_in_period }}</strong></div>
            </div>
            <div class="table-scroll">
              <table cTable [small]="true" class="mb-0">
                <thead><tr><th>{{ 'mycards.partner.kyc_status' | translate }}</th><th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th></tr></thead>
                <tbody>
                  @for (r of kycRows(); track r.value) {
                    <tr>
                      <td><c-badge [color]="kycColor(r.value)">{{ r.label | translate }}</c-badge></td>
                      <td class="text-end">{{ r.count }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.snapshot_hint' | translate }}</div>
            <div class="table-scroll mt-3">
              <table cTable [small]="true" class="mb-0">
                <thead><tr><th>{{ 'mycards.partner.account_status' | translate }}</th><th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th></tr></thead>
                <tbody>
                  @for (r of accountStatusRows(); track r.value) {
                    <tr><td>{{ r.label | translate }}</td><td class="text-end">{{ r.count }}</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </c-card-body>
        </c-card>
      </c-col>

      <!-- Cards -->
      <c-col xs="12" lg="6">
        <c-card class="h-100">
          <c-card-header><strong>{{ 'card_partner_admin.cards_title' | translate }}</strong></c-card-header>
          <c-card-body>
            <div class="d-flex flex-wrap gap-4 mb-3 small">
              <div>{{ 'card_partner_admin.cards_total' | translate }}: <strong>{{ o.cards.total }}</strong></div>
              <div>{{ 'card_partner_admin.cards_created' | translate }}: <strong>{{ o.cards.created_in_period }}</strong></div>
              <div>{{ 'card_partner_admin.kpi_activations' | translate }}: <strong>{{ o.cards.activations_in_period }}</strong></div>
            </div>
            <div class="table-scroll">
              <table cTable [small]="true" class="mb-0">
                <thead><tr><th>{{ 'card_partner_admin.col_card_status' | translate }}</th><th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th></tr></thead>
                <tbody>
                  @for (r of cardStatusRows(); track r.value) {
                    <tr><td><c-badge [color]="cardColor(r.value)">{{ r.label | translate }}</c-badge></td><td class="text-end">{{ r.count }}</td></tr>
                  }
                </tbody>
              </table>
            </div>
            <div class="table-scroll mt-3">
              <table cTable [small]="true" class="mb-0">
                <thead><tr><th>{{ 'mycards.partner.physical_card' | translate }}</th><th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th></tr></thead>
                <tbody>
                  @for (r of physicalRows(); track r.value) {
                    <tr><td>{{ r.label | translate }}</td><td class="text-end">{{ r.count }}</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <c-row class="g-3 mb-3">
      <!-- Transactions -->
      <c-col xs="12" xl="7">
        <c-card class="h-100">
          <c-card-header><strong>{{ 'card_partner_admin.tx_title' | translate }}</strong> <span class="text-medium-emphasis small ms-2">{{ o.transactions.count }}</span></c-card-header>
          <c-card-body>
            @if (o.transactions.by_type_status.length === 0) {
              <div class="text-medium-emphasis">{{ 'card_partner_admin.no_data' | translate }}</div>
            } @else {
              <div class="table-scroll">
                <table cTable [small]="true" class="mb-0">
                  <thead>
                    <tr>
                      <th>{{ 'card_partner_admin.col_type' | translate }}</th>
                      <th>{{ 'mycards.partner.status' | translate }}</th>
                      <th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th>
                      <th class="text-end">{{ 'card_partner_admin.col_volume' | translate }} (USD)</th>
                      <th class="text-end">{{ 'mycards.partner.fee' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (r of o.transactions.by_type_status; track (r.type ?? '') + (r.status ?? '')) {
                      <tr>
                        <td>{{ keyOr('type', r.type) | translate }}</td>
                        <td><c-badge [color]="statusColor(r.status)">{{ keyOr('tx_status', r.status) | translate }}</c-badge></td>
                        <td class="text-end">{{ r.count }}</td>
                        <td class="text-end text-nowrap">{{ money(r.volume) }}</td>
                        <td class="text-end text-nowrap">{{ money(r.fees) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
              <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.spend_hint' | translate }}</div>
            }
          </c-card-body>
        </c-card>
      </c-col>

      <!-- Top categories -->
      <c-col xs="12" xl="5">
        <c-card class="h-100">
          <c-card-header><strong>{{ 'card_partner_admin.top_categories' | translate }}</strong></c-card-header>
          <c-card-body>
            @if (o.transactions.top_categories.length === 0) {
              <div class="text-medium-emphasis">{{ 'card_partner_admin.no_data' | translate }}</div>
            } @else {
              <div class="table-scroll">
                <table cTable [small]="true" class="mb-0">
                  <thead>
                    <tr>
                      <th>{{ 'card_partner_admin.col_category' | translate }}</th>
                      <th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th>
                      <th class="text-end">{{ 'card_partner_admin.col_volume' | translate }} (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (c of o.transactions.top_categories; track c.category) {
                      <tr>
                        <td>{{ c.category || ('card_partner_admin.uncategorised' | translate) }}</td>
                        <td class="text-end">{{ c.count }}</td>
                        <td class="text-end text-nowrap">{{ money(c.volume) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
              <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.mcc_hint' | translate }}</div>
            }
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <!-- Deposits + withdrawals -->
    <c-row class="g-3 mb-4">
      @for (block of [{ key: 'deposits', data: o.deposits }, { key: 'withdrawals', data: o.withdrawals }]; track block.key) {
        <c-col xs="12" xl="6">
          <c-card class="h-100">
            <c-card-header>
              <strong>{{ 'card_partner_admin.' + block.key + '_title' | translate }}</strong>
              <span class="text-medium-emphasis small ms-2">{{ block.data.count }}</span>
            </c-card-header>
            <c-card-body>
              @if (block.data.by_status.length === 0) {
                <div class="text-medium-emphasis">{{ 'card_partner_admin.no_data' | translate }}</div>
              } @else {
                <div class="d-flex flex-wrap gap-3 mb-3 small">
                  @for (t of block.data.settled_by_token; track t.currency) {
                    <div>{{ t.currency }}: <strong>{{ crypto(t.volume) }}</strong> ({{ t.count }})</div>
                  }
                  @for (t of block.data.refunded_by_token; track t.currency) {
                    <div class="text-warning">{{ 'mycards.partner.transfer_status_REFUNDED' | translate }} {{ t.currency }}: {{ crypto(t.volume) }} ({{ t.count }})</div>
                  }
                </div>
                <div class="table-scroll">
                  <table cTable [small]="true" class="mb-0">
                    <thead>
                      <tr>
                        <th>{{ 'mycards.partner.network' | translate }}</th>
                        <th>{{ 'card_partner_admin.col_token' | translate }}</th>
                        <th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th>
                        <th class="text-end">{{ 'card_partner_admin.col_volume' | translate }}</th>
                        <th class="text-end">{{ 'mycards.partner.fee' | translate }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (r of block.data.by_chain_token; track (r.chain ?? '') + (r.currency ?? '')) {
                        <tr>
                          <td>{{ chainLabel(r.chain) }}</td>
                          <td>{{ r.currency }}</td>
                          <td class="text-end">{{ r.count }}</td>
                          <td class="text-end text-nowrap">{{ crypto(r.volume) }}</td>
                          <td class="text-end text-nowrap">{{ crypto(r.fees ?? null) }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                <div class="table-scroll mt-3">
                  <table cTable [small]="true" class="mb-0">
                    <thead>
                      <tr>
                        <th>{{ 'mycards.partner.status' | translate }}</th>
                        <th>{{ 'card_partner_admin.col_token' | translate }}</th>
                        <th class="text-end">{{ 'card_partner_admin.col_count' | translate }}</th>
                        <th class="text-end">{{ 'card_partner_admin.col_volume' | translate }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (r of block.data.by_status; track (r.status ?? '') + (r.currency ?? '')) {
                        <tr>
                          <td><c-badge [color]="statusColor(r.status)">{{ (r.status ? keyOr('transfer_status', r.status) : 'card_partner_admin.status_unknown') | translate }}</c-badge></td>
                          <td>{{ r.currency }}</td>
                          <td class="text-end">{{ r.count }}</td>
                          <td class="text-end text-nowrap">{{ crypto(r.volume) }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.transfer_hint' | translate }}</div>
              }
            </c-card-body>
          </c-card>
        </c-col>
      }
    </c-row>
  }

  <!-- Ranking -->
  <c-card class="mb-4">
    <c-card-header class="d-flex flex-wrap align-items-center gap-2">
      <strong class="me-auto">{{ 'card_partner_admin.ranking_title' | translate }}</strong>
      <span class="small text-medium-emphasis">{{ total() }} {{ 'card_partner_admin.accounts' | translate }}</span>
      <button cButton color="secondary" variant="outline" size="sm" [disabled]="exporting()" (click)="exportCsv()">
        @if (exporting()) { <c-spinner size="sm" class="me-1"></c-spinner> }
        {{ 'card_partner_admin.export_csv' | translate }}
      </button>
    </c-card-header>
    <c-card-body>
      <c-row class="g-2 mb-3">
        <c-col xs="12" md="4">
          <label cLabel for="cpSearch" class="small mb-1">{{ 'card_partner_admin.search' | translate }}</label>
          <input cFormControl sizing="sm" id="cpSearch" type="search" [placeholder]="'card_partner_admin.search_placeholder' | translate"
                 (input)="onSearch($any($event.target).value)" />
        </c-col>
        <c-col xs="6" md="2">
          <label cLabel for="cpKyc" class="small mb-1">{{ 'mycards.partner.kyc_status' | translate }}</label>
          <select cSelect sizing="sm" id="cpKyc" [ngModel]="kycFilter()" (ngModelChange)="setFilter('kyc', $event)">
            <option value="">{{ 'card_partner_admin.all' | translate }}</option>
            @for (s of kycStatuses; track s) {
              <option [value]="s">{{ keyOr('kyc', s) | translate }}</option>
            }
          </select>
        </c-col>
        <c-col xs="6" md="2">
          <label cLabel for="cpCard" class="small mb-1">{{ 'card_partner_admin.has_card' | translate }}</label>
          <select cSelect sizing="sm" id="cpCard" [ngModel]="hasCardFilter()" (ngModelChange)="setFilter('card', $event)">
            <option value="">{{ 'card_partner_admin.all' | translate }}</option>
            <option value="yes">{{ 'card_partner_admin.answer_yes' | translate }}</option>
            <option value="no">{{ 'card_partner_admin.answer_no' | translate }}</option>
          </select>
        </c-col>
        <c-col xs="6" md="2">
          <label cLabel for="cpLinked" class="small mb-1">{{ 'card_partner_admin.linked_filter' | translate }}</label>
          <select cSelect sizing="sm" id="cpLinked" [ngModel]="linkedFilter()" (ngModelChange)="setFilter('linked', $event)">
            <option value="">{{ 'card_partner_admin.all' | translate }}</option>
            <option value="yes">{{ 'card_partner_admin.linked' | translate }}</option>
            <option value="no">{{ 'card_partner_admin.unlinked' | translate }}</option>
          </select>
        </c-col>
        <c-col xs="6" md="2">
          <label cLabel for="cpPerPage" class="small mb-1">{{ 'card_partner_admin.per_page' | translate }}</label>
          <select cSelect sizing="sm" id="cpPerPage" [ngModel]="perPage()" (ngModelChange)="setPerPage($event)">
            @for (n of perPageOptions; track n) {
              <option [ngValue]="n">{{ n === 25 ? ('card_partner_admin.top_25' | translate) : n }}</option>
            }
          </select>
        </c-col>
        <c-col xs="12">
          <c-form-check>
            <input cFormCheckInput id="cpActive" type="checkbox" [ngModel]="activeOnly()" (ngModelChange)="setActiveOnly($event)" />
            <label cFormCheckLabel for="cpActive" class="small">{{ 'card_partner_admin.active_only' | translate }}</label>
          </c-form-check>
        </c-col>
      </c-row>

      <div class="table-scroll" [class.opacity-50]="rankingLoading()">
        <table cTable hover [small]="true" class="align-middle mb-0 ranking-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{{ 'card_partner_admin.col_account' | translate }}</th>
              <th>{{ 'card_partner_admin.col_our_user' | translate }}</th>
              <th class="sortable" (click)="sortBy('kyc')">{{ 'mycards.partner.kyc_status' | translate }} {{ sortIcon('kyc') }}</th>
              <th>{{ 'card_partner_admin.col_card' | translate }}</th>
              <th class="sortable" (click)="sortBy('activated_at')">{{ 'mycards.partner.card_activated' | translate }} {{ sortIcon('activated_at') }}</th>
              <th class="sortable text-end" (click)="sortBy('spend')">{{ 'card_partner_admin.kpi_spend' | translate }} {{ sortIcon('spend') }}</th>
              <th class="sortable text-end" (click)="sortBy('fees')">{{ 'card_partner_admin.kpi_fees' | translate }} {{ sortIcon('fees') }}</th>
              <th class="sortable text-end" (click)="sortBy('tx_count')">{{ 'card_partner_admin.col_tx_count' | translate }} {{ sortIcon('tx_count') }}</th>
              <th class="sortable text-end" (click)="sortBy('deposits')">{{ 'card_partner_admin.kpi_deposits' | translate }} {{ sortIcon('deposits') }}</th>
              <th class="sortable text-end" (click)="sortBy('withdrawals')">{{ 'card_partner_admin.col_withdrawals' | translate }} {{ sortIcon('withdrawals') }}</th>
              <th class="sortable" (click)="sortBy('last_tx')">{{ 'card_partner_admin.col_last_tx' | translate }} {{ sortIcon('last_tx') }}</th>
            </tr>
          </thead>
          <tbody>
            @for (r of rows(); track r.partner_user_id; let i = $index) {
              <tr class="clickable" (click)="openDetail(r)">
                <td class="text-medium-emphasis">{{ (page() - 1) * perPage() + i + 1 }}</td>
                <td>
                  <div class="text-nowrap">{{ r.email || '—' }}</div>
                  @if (r.nickname) { <div class="small text-medium-emphasis">{{ r.nickname }}</div> }
                </td>
                <td>
                  @if (r.user) {
                    <a href="#" (click)="$event.preventDefault(); openUser($event, r)">#{{ r.user.id }}</a>
                    @if (r.user.email && r.user.email !== r.email) { <div class="small text-medium-emphasis">{{ r.user.email }}</div> }
                  } @else {
                    <c-badge color="warning">{{ 'card_partner_admin.unlinked' | translate }}</c-badge>
                  }
                </td>
                <td><c-badge [color]="kycColor(r.kyc_status)">{{ keyOr('kyc', r.kyc_status) | translate }}</c-badge></td>
                <td>
                  @if (r.card_status) {
                    <c-badge [color]="cardColor(r.card_status)">{{ keyOr('card', r.card_status) | translate }}</c-badge>
                    @if (r.cards_count > 1) { <span class="small text-medium-emphasis ms-1">×{{ r.cards_count }}</span> }
                  } @else { — }
                </td>
                <td class="text-nowrap">{{ date(r.card_activated_at) }}</td>
                <td class="text-end text-nowrap fw-semibold">{{ money(r.spend) }}</td>
                <td class="text-end text-nowrap">{{ money(r.fees) }}</td>
                <td class="text-end">{{ r.tx_count }}</td>
                <td class="text-end text-nowrap" [title]="'USDT ' + r.deposits.USDT + ' · USDC ' + r.deposits.USDC">
                  {{ isZero(r.deposits.total) ? '—' : money(r.deposits.total) }}
                </td>
                <td class="text-end text-nowrap" [title]="'USDT ' + r.withdrawals.USDT + ' · USDC ' + r.withdrawals.USDC">
                  {{ isZero(r.withdrawals.total) ? '—' : money(r.withdrawals.total) }}
                </td>
                <td class="text-nowrap">{{ dateTime(r.last_tx_at) }}</td>
              </tr>
            } @empty {
              <tr><td colspan="12" class="text-center text-medium-emphasis py-3">
                {{ (rankingLoading() ? 'card_partner_admin.loading' : 'card_partner_admin.no_accounts') | translate }}
              </td></tr>
            }
          </tbody>
        </table>
      </div>
      <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.ranking_hint' | translate }}</div>

      @if (totalPages() > 1) {
        <div class="d-flex flex-wrap align-items-center justify-content-center gap-2 mt-3">
          <button cButton color="secondary" variant="outline" size="sm" [disabled]="page() <= 1" (click)="goToPage(page() - 1)">‹</button>
          <span class="small">{{ 'card_partner_admin.page_of' | translate: { page: page(), pages: totalPages() } }}</span>
          <button cButton color="secondary" variant="outline" size="sm" [disabled]="page() >= totalPages()" (click)="goToPage(page() + 1)">›</button>
        </div>
      }
    </c-card-body>
  </c-card>
</c-container>
